import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {ApplicationConditionDTO} from '../../../../../../models/application/ApplicationConditionDTO';
import {MatDialog} from '@angular/material/dialog';
import {UtilService} from '../../../../../../../common/services/util.service';
import {NameValueCodeDTO} from '../../../../../../../common/models/NameValueCodeDTO';
import {ConfirmationDialogComponent} from '../../../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {UserAuthorisationService} from '../../../../../../../common/services/user-authorisation.service';
import {ApplicationConditionDialogComponent} from '../../../../../dialogs/application-condition-dialog/application-condition-dialog.component';

@Component({
  selector: 'app-person-conditions',
  templateUrl: './person-conditions.component.html',
  styleUrls: ['./person-conditions.component.scss']
})
export class PersonConditionsComponent implements OnChanges {
  @Input() conditions: ApplicationConditionDTO[];
  @Input() diagnosisGroups: NameValueCodeDTO[];
  @Input() isApplicationLocked: boolean;

  @Output() conditionsChange = new EventEmitter<{
    conditions: ApplicationConditionDTO[];
  }>();

  hasPermissions: boolean;

  constructor(
    private dialog: MatDialog,
    private utilService: UtilService,
    private userAuthorisationService: UserAuthorisationService
  ) {
    this.diagnosisGroups = [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isApplicationLocked) {
      this.isApplicationLocked = changes.isApplicationLocked.currentValue;
      this.setPermission();
    }
  }

  setPermission(): void {
    const hasRole: boolean = this.userAuthorisationService.hasOnBoardingQC() || this.userAuthorisationService.hasRoleEnterpriseManager();
    this.hasPermissions = hasRole && !this.isApplicationLocked;
  }

  getConditionString(condition: ApplicationConditionDTO): string {
    const questionCode = condition.questionCode ? 'Q' + condition.questionCode + ' - ' : '';
    const conditionCode = condition.conditionCode ? condition.conditionCode + ' - ' : '';
    const providerName = condition.provider?.providerName ? condition.provider?.providerName + ' - ' : '';
    const firstTreatmentDate = condition.firstTreatmentDate ? `first treated ${this.formatDate(condition.firstTreatmentDate)} - ` : '';
    const lastTreatmentDate = condition.lastTreatmentDate ? `last treated ${this.formatDate(condition.lastTreatmentDate)} - ` : '';
    const currentIndicator = condition.isCurrent ? '(Current)' : '';

    let conditionDisplayString = `${questionCode}${conditionCode}${providerName}${firstTreatmentDate}${lastTreatmentDate}${currentIndicator}`;

    if (condition.medication) {
      conditionDisplayString += ` - Medication: ${condition.medication.drugCode} ${condition.medication.drugName}`;
    }

    return conditionDisplayString;
  }

  onEditCondition(condition: ApplicationConditionDTO): void {
    const dialogRef = this.dialog.open(ApplicationConditionDialogComponent, {
      width: '40vw',
      data: {
        condition,
        diagnosisGroups: this.diagnosisGroups
      }
    });

    dialogRef.afterClosed().subscribe((updatedCondition: ApplicationConditionDTO) => {
      if (updatedCondition) {
        this.updateConditions(updatedCondition);
      }
    });
  }

  onAddCondition(): void {
    const dialogRef = this.dialog.open(ApplicationConditionDialogComponent, {
      width: '40vw',
      data: {
        diagnosisGroups: this.diagnosisGroups
      }
    });

    dialogRef.afterClosed().subscribe((conditionDTO: ApplicationConditionDTO) => {
      if (conditionDTO) {
        this.updateConditions(conditionDTO);
      }
    });
  }

  onDeleteCondition(condition: ApplicationConditionDTO): void {
    const displayStr = this.getConditionString(condition);
    const confirmationMessage = `Are you sure you want to delete condition ${displayStr}?`;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: confirmationMessage
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.deleteCondition(condition);
      }
    });
  }

  private deleteCondition(conditionToDelete: ApplicationConditionDTO): void {
    this.conditions = this.conditions.filter(
      (condition) => condition.applicationConditionId !== conditionToDelete.applicationConditionId
    );
    this.emitUpdatedDetails();
  }

  private emitUpdatedDetails(): void {
    this.conditionsChange.emit({
      conditions: this.conditions
    });
  }

  private updateConditions(conditionUpdated: ApplicationConditionDTO): void {
    const index = this.conditions.findIndex(
      (condition: ApplicationConditionDTO) => condition.applicationConditionId === conditionUpdated.applicationConditionId
    );
    if (index !== -1) {
      this.conditions[index] = {...conditionUpdated};
    } else {
      this.conditions.push(conditionUpdated);
    }
    this.emitUpdatedDetails();
  }

  private formatDate(date: Date | string): string {
    return date ? this.utilService.dateFormat(date) : 'unknown';
  }
}
